import { BLOCK_THREAD_NODE, INLINE_THREAD_MARK } from '@/lib/snapshot/strip-comment-markup';
import type { Fragment, Mark, Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Transform } from '@tiptap/pm/transform';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';

/*
 * The diff is computed on documents with comment markup stripped (see stripCommentMarkup), so
 * comments never show up as changes. The diff document then equals the stripped newer version,
 * and the newer version's comment anchors are put back without being drawn as changes:
 *
 * - Inline comments: their marks are re-added with plain AddMarkSteps. toDiff() only renders
 *   attributed ChangeTrackingSteps, and marks take up no positions, so diff highlights stay aligned.
 * - Block comments: restoring the wrapper node would shift every later position and misalign the
 *   diff, so the block is decorated with `data-thread-id` instead. That is all the floating
 *   comment cards need to anchor to.
 *
 * Positions below are "stripped" positions: positions in the document without blockThread wrappers.
 */

export type InlineCommentAnchor = { from: number; to: number; mark: Mark };
export type BlockCommentAnchor = { pos: number; threadId: string };

type CommentAnchors = { inline: InlineCommentAnchor[]; blocks: BlockCommentAnchor[] };

function isBlockThread(node: Node): boolean {
    return node.type.name === BLOCK_THREAD_NODE;
}

function strippedContentSize(fragment: Fragment): number {
    let size = 0;
    fragment.forEach(child => {
        size += strippedNodeSize(child);
    });
    return size;
}

function strippedNodeSize(node: Node): number {
    if (isBlockThread(node)) {
        return strippedContentSize(node.content);
    }
    return node.isLeaf ? node.nodeSize : 2 + strippedContentSize(node.content);
}

function collectFromFragment(fragment: Fragment, strippedStart: number, out: CommentAnchors): void {
    let pos = strippedStart;
    fragment.forEach(child => {
        if (isBlockThread(child)) {
            // The thread wraps one block (several in legacy wrapping); anchoring the first is enough.
            out.blocks.push({ pos, threadId: String(child.attrs['data-thread-id'] ?? '') });
            collectFromFragment(child.content, pos, out);
            pos += strippedNodeSize(child);
            return;
        }
        const size = strippedNodeSize(child);
        for (const mark of child.marks) {
            if (mark.type.name === INLINE_THREAD_MARK) {
                out.inline.push({ from: pos, to: pos + size, mark });
            }
        }
        if (!child.isLeaf) {
            collectFromFragment(child.content, pos + 1, out);
        }
        pos += size;
    });
}

/** Comment anchors of `doc`, in positions of the same document with comment markup stripped. */
export function collectCommentAnchors(doc: Node): CommentAnchors {
    const anchors: CommentAnchors = { inline: [], blocks: [] };
    collectFromFragment(doc.content, 0, anchors);
    return anchors;
}

/**
 * Re-adds `newerDoc`'s inline comment marks to the diff transform and returns its block comment
 * anchors. `strippedNewerDoc` is what the transform's document should equal; if it does not, no
 * anchors are restored rather than restoring them in the wrong places.
 */
export function restoreCommentAnchors(tr: Transform, newerDoc: Node, strippedNewerDoc: Node): BlockCommentAnchor[] {
    if (!tr.doc.eq(strippedNewerDoc)) {
        return [];
    }
    const anchors = collectCommentAnchors(newerDoc);
    for (const { from, to, mark } of anchors.inline) {
        tr.addMark(from, to, mark);
    }
    return anchors.blocks;
}

const diffCommentAnchorsKey = new PluginKey<DecorationSet>('diffCommentAnchors');

/** Decorates commented blocks while a diff is shown. Positions are in the diff document. */
export function showDiffBlockCommentAnchors(editor: Editor, anchors: BlockCommentAnchor[]): void {
    clearDiffBlockCommentAnchors(editor);
    if (anchors.length === 0) {
        return;
    }
    editor.registerPlugin(
        new Plugin({
            key: diffCommentAnchorsKey,
            props: {
                decorations(state) {
                    if (!editor.storage.snapshotCompare?.isPreviewing) {
                        return null;
                    }
                    const decorations = anchors.flatMap(({ pos, threadId }) => {
                        const node = state.doc.nodeAt(pos);
                        return node && !node.isInline
                            ? [
                                  Decoration.node(pos, pos + node.nodeSize, {
                                      'data-thread-id': threadId,
                                      class: 'tiptap-thread tiptap-thread--block',
                                  }),
                              ]
                            : [];
                    });
                    return DecorationSet.create(state.doc, decorations);
                },
            },
        }),
    );
}

export function clearDiffBlockCommentAnchors(editor: Editor): void {
    if (!editor.isDestroyed) {
        editor.unregisterPlugin(diffCommentAnchorsKey);
    }
}

/**
 * Maps a position in the stripped document (the diff document, when comparing against the current
 * document) to the same place in `doc`, which still has its blockThread wrappers.
 */
export function mapStrippedPositionToDocument(doc: Node, strippedPos: number): number {
    function mapIn(fragment: Fragment, originalStart: number, strippedStart: number): number {
        let original = originalStart;
        let stripped = strippedStart;
        let result: number | null = null;
        fragment.forEach(child => {
            if (result !== null) {
                return;
            }
            const size = strippedNodeSize(child);
            if (strippedPos <= stripped) {
                result = original + (strippedPos - stripped);
                return;
            }
            if (strippedPos < stripped + size) {
                if (isBlockThread(child)) {
                    result = mapIn(child.content, original + 1, stripped);
                } else if (child.isLeaf) {
                    result = original + (strippedPos - stripped);
                } else {
                    result = mapIn(child.content, original + 1, stripped + 1);
                }
                return;
            }
            original += child.nodeSize;
            stripped += size;
        });
        return result ?? original + (strippedPos - stripped);
    }
    return mapIn(doc.content, 0, 0);
}
